import React, { useRef, useState } from 'react';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Button, Input, Tooltip, Typography } from '@duxton-web/ui';
import { styled } from 'styled-components';

import { DatadogMonitor } from './types';
import {
  DatadogMonitorStatus,
  normalizeDatadogMonitorStatus,
} from '@/features/variable/canary-monitor-status/utils';
import Service from '@/lib/App';

interface MonitorsSectionProps {
  monitors: DatadogMonitor[];
  onChange: (monitors: DatadogMonitor[]) => void;
  disabled?: boolean;
  variableName: string;
}

type TestState = {
  loading: boolean;
  result?: { valid: boolean; state?: string | number; error?: string };
};

type MonitorStatusConfig = {
  icon: React.ReactNode;
  label: string;
  color: string;
};

function getMonitorStatusConfig(state: unknown): MonitorStatusConfig {
  const normalized = normalizeDatadogMonitorStatus(state);
  switch (normalized) {
    case 'OK':
      return {
        icon: <CheckCircleOutlined {...({} as any)} />,
        label: 'OK',
        color: '#52c41a',
      };
    case 'Warn':
      return {
        icon: <ExclamationCircleOutlined {...({} as any)} />,
        label: 'Warning',
        color: '#fa8c16',
      };
    case 'Alert':
      return {
        icon: <CloseCircleOutlined {...({} as any)} />,
        label: 'Alert',
        color: '#ff4d4f',
      };
    case 'Data unavailable':
    case 'Not checked':
    default:
      return {
        icon: <MinusCircleOutlined {...({} as any)} />,
        label: normalized as DatadogMonitorStatus,
        color: '#8c8c8c',
      };
  }
}

const MonitorsSection: React.FC<MonitorsSectionProps> = ({
  monitors,
  onChange,
  disabled = false,
  variableName,
}) => {
  const [testStates, setTestStates] = useState<Record<string, TestState>>({});
  // Keep a ref so async callbacks always see the latest monitors list.
  const monitorsRef = useRef(monitors);
  monitorsRef.current = monitors;

  const handleMonitorChange = (
    index: number,
    field: keyof DatadogMonitor,
    value: string | null,
  ) => {
    const updated = [...monitors];
    const monitorKey = monitors[index]?.id ?? String(index);
    updated[index] = {
      ...updated[index],
      [field]: value ?? '',
      ...(field === 'monitorUrl'
        ? { monitorValidated: undefined, monitorState: undefined }
        : {}),
    };
    onChange(updated);
    if (field === 'monitorUrl') {
      setTestStates((prev) => {
        const next = { ...prev };
        delete next[monitorKey];
        return next;
      });
    }
  };

  const handleAddMonitor = () => {
    onChange([
      ...monitors,
      { id: crypto.randomUUID(), displayName: '', monitorUrl: '' },
    ]);
  };

  const handleRemoveMonitor = (index: number) => {
    const monitorKey = monitors[index]?.id ?? String(index);
    onChange(monitors.filter((_, i) => i !== index));
    setTestStates((prev) => {
      const next = { ...prev };
      delete next[monitorKey];
      return next;
    });
  };

  const handleTestMonitor = async (index: number) => {
    const monitor = monitors[index];
    const monitorId = monitor?.id;
    const monitorKey = monitorId ?? String(index);
    const url = monitor?.monitorUrl?.trim();
    if (!url) return;

    setTestStates((prev) => ({
      ...prev,
      [monitorKey]: { loading: true },
    }));

    const resp = await Service.planner!.validateCanaryQuery({
      type: 'monitor',
      variable_name: variableName,
      monitor_url: url,
    });

    setTestStates((prev) => ({
      ...prev,
      [monitorKey]: {
        loading: false,
        result: {
          valid: resp.valid,
          state: resp.monitorState,
          error: resp.error,
        },
      },
    }));

    // Merge result into the latest monitors list by stable ID to avoid
    // overwriting edits made while the async request was in-flight.
    onChange(
      monitorsRef.current.map((m) =>
        m.id === monitorId
          ? {
              ...m,
              monitorValidated: resp.valid,
              monitorState: resp.monitorState,
            }
          : m,
      ),
    );
  };

  return (
    <Wrapper>
      <Header>
        <Typography.Text strong>Datadog Monitors</Typography.Text>
        <Typography.Text type="secondary">
          Existing monitors that stop the rollout on Alert
        </Typography.Text>
      </Header>
      <TableContainer>
        <TableInner>
          <TableHead>
            <ColName>Monitor Name</ColName>
            <ColUrl>Datadog Monitor URL</ColUrl>
            <ColStatus>Status</ColStatus>
            <ColActions />
          </TableHead>
          {monitors.map((monitor, index) => {
            const monitorKey = monitor.id ?? String(index);
            const ts = testStates[monitorKey];
            const hasUrl = !!monitor.monitorUrl?.trim();
            return (
              <div key={monitorKey}>
                <TableRow>
                  <ColName>
                    <Input
                      placeholder="Name (optional)"
                      value={monitor.displayName ?? ''}
                      disabled={disabled}
                      onChange={(e) =>
                        handleMonitorChange(
                          index,
                          'displayName',
                          e.target.value,
                        )
                      }
                      size="small"
                    />
                  </ColName>
                  <ColUrl>
                    <Input
                      placeholder="https://app.datadoghq.com/monitors/12345"
                      value={monitor.monitorUrl ?? ''}
                      disabled={disabled}
                      onChange={(e) =>
                        handleMonitorChange(index, 'monitorUrl', e.target.value)
                      }
                      size="small"
                    />
                  </ColUrl>
                  <ColStatus>
                    {ts?.result?.valid && ts.result.state != null ? (
                      (() => {
                        const cfg = getMonitorStatusConfig(ts.result.state);
                        return (
                          <MonitorStatusTag color={cfg.color}>
                            <span style={{ color: cfg.color }}>{cfg.icon}</span>
                            {cfg.label}
                          </MonitorStatusTag>
                        );
                      })()
                    ) : (
                      <StatusPlaceholder>—</StatusPlaceholder>
                    )}
                  </ColStatus>
                  <ColActions>
                    <Tooltip title="Validate monitor">
                      <span>
                        <Button
                          type="text"
                          size="small"
                          icon={
                            ts?.loading ? (
                              <LoadingOutlined {...({} as any)} />
                            ) : (
                              <ThunderboltOutlined {...({} as any)} />
                            )
                          }
                          onClick={() => handleTestMonitor(index)}
                          disabled={disabled || !hasUrl}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove monitor">
                      <span>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined {...({} as any)} />}
                          onClick={() => handleRemoveMonitor(index)}
                          disabled={disabled}
                        />
                      </span>
                    </Tooltip>
                  </ColActions>
                </TableRow>
                {ts?.result && !ts.loading && !ts.result.valid && (
                  <TestResultRow>
                    <ErrorText>{ts.result.error}</ErrorText>
                  </TestResultRow>
                )}
              </div>
            );
          })}
        </TableInner>
      </TableContainer>
      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined {...({} as any)} />}
        onClick={handleAddMonitor}
        disabled={disabled}
        style={{ alignSelf: 'flex-start' }}
      >
        Add Monitor
      </Button>
      <AlertNote>
        Only <strong>Alert</strong> status stops the rollout. Warn and No Data
        do not.
      </AlertNote>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const TableContainer = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  overflow-x: auto;
  overflow-y: hidden;
`;

const TableInner = styled.div`
  min-width: 400px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 8px;
  gap: 8px;
`;

const TableHead = styled(Row)`
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
  font-weight: 600;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
  padding: 8px 8px;
`;

const TableRow = styled(Row)`
  border-bottom: 1px solid #f0f0f0;
`;

const ColName = styled.div`
  width: 130px;
  flex-shrink: 0;
`;

const ColUrl = styled.div`
  flex: 1;
  min-width: 160px;
  overflow: hidden;
`;

const ColStatus = styled.div`
  width: 110px;
  flex-shrink: 0;
`;

const ColActions = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 2px;
`;

const TestResultRow = styled.div`
  padding: 4px 8px 8px 8px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 12px;
`;

const MonitorStatusTag = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${(p) => p.color};
  font-weight: 500;
`;

const StatusPlaceholder = styled.span`
  color: #bfbfbf;
  font-size: 12px;
`;

const ErrorText = styled.span`
  color: #ff4d4f;
`;

const AlertNote = styled.div`
  font-size: 11px;
  color: #8c8c8c;
`;

export default MonitorsSection;
